import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useParams} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import Button from '../../uiToolkit/Buttons/Button';
import IconButton from '../../uiToolkit/Buttons/IconButton';
import LoadingSpinner from '../../uiToolkit/LoadingSpinner/LoadingSpinner';
import ConfirmationModal from '../../uiToolkit/ConfirmationModal';
import LearnersEmpty from '../LearnersTable/LearnersEmpty';
import LearnersSearch from '../LearnersTable/LearnersSearch';
import ModalFade, {
  useModalFade,
} from '../../pages/Microreels/List/components/ModalFade';
import GroupFormModal from './GroupFormModal';
import GroupMembersModal from './GroupMembersModal';
import LearnerGroupsStore from './store/LearnerGroupsStore';
import {LearnerGroup} from '../../data/models/Groups/LearnerGroup';
import {deleteIcon, editIcon, subUsersIc} from '../../assets/images';
import {getFirstCapitals} from '../../utils/getFirstCapitals';
import {learnerGroupsText} from './learnerGroupsText';
import '../../uiToolkit/DataTable/styles.css';
import styles from './GroupsManager.module.css';

interface Props {
  learnerGroupsStore?: LearnerGroupsStore;
}

const groupsColumns = (
  onManageMembers: (group: LearnerGroup) => void,
  onEditGroup: (group: LearnerGroup) => void,
  onDeleteGroup: (group: LearnerGroup) => void,
) => [
  {
    name: learnerGroupsText.columnName,
    selector: (row: LearnerGroup) => row.name,
    cell: (row: LearnerGroup) => (
      <div className="DataTableImageNameContainer">
        <span className={styles.avatar}>{getFirstCapitals(row.name)}</span>
        <div className="DataTableText">{row.name}</div>
      </div>
    ),
  },
  {
    name: learnerGroupsText.columnDescription,
    selector: (row: LearnerGroup) => row.description || '-',
    cell: (row: LearnerGroup) => (
      <div className="DataTableText">{row.description || '-'}</div>
    ),
  },
  {
    name: learnerGroupsText.columnMembers,
    selector: (row: LearnerGroup) => row.learnersNumber,
    cell: (row: LearnerGroup) => (
      <div className="DataTableText">{row.learnersNumber}</div>
    ),
    id: 'members',
    center: true,
  },
  {
    name: learnerGroupsText.columnActions,
    cell: (row: LearnerGroup) => (
      <div className={styles.rowActions}>
        <IconButton
          onClick={() => onManageMembers(row)}
          icon={subUsersIc}
          tooltip={learnerGroupsText.manageMembers}
        />
        <IconButton
          onClick={() => onEditGroup(row)}
          icon={editIcon}
          tooltip={learnerGroupsText.edit}
        />
        <IconButton
          onClick={() => onDeleteGroup(row)}
          icon={deleteIcon}
          tooltip={learnerGroupsText.delete}
        />
      </div>
    ),
    id: 'actions',
  },
];

const GroupsManager = inject('learnerGroupsStore')(
  observer(({learnerGroupsStore}: Props) => {
    const {organizationId} = useParams();

    const [search, setSearch] = useState('');
    const [formShown, setFormShown] = useState(false);
    const [membersShown, setMembersShown] = useState(false);
    const [groupToEdit, setGroupToEdit] = useState<LearnerGroup>();
    const [groupToDelete, setGroupToDelete] = useState<LearnerGroup>();
    const [groupToManage, setGroupToManage] = useState<LearnerGroup>();

    const formFade = useModalFade(formShown);
    const membersFade = useModalFade(membersShown);
    const deleteFade = useModalFade(groupToDelete !== undefined);

    useEffect(() => {
      if (organizationId) {
        learnerGroupsStore!.fetchGroups(organizationId);
      }
    }, [organizationId]);

    const groups = learnerGroupsStore!.groups.filter(group =>
      group.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

    const onNewGroup = () => {
      setGroupToEdit(undefined);
      setFormShown(true);
    };

    const onEditGroup = (group: LearnerGroup) => {
      setGroupToEdit(group);
      setFormShown(true);
    };

    const onManageMembers = (group: LearnerGroup) => {
      setGroupToManage(group);
      setMembersShown(true);
    };

    const closeMembers = () => {
      setMembersShown(false);
      if (organizationId) {
        learnerGroupsStore!.fetchGroups(organizationId);
      }
    };

    const onSubmitForm = async (name: string, description: string | null) => {
      if (!organizationId) return;
      const saved = groupToEdit
        ? await learnerGroupsStore!.editGroup(organizationId, groupToEdit.id, {
            name,
            description,
          })
        : await learnerGroupsStore!.createGroup(organizationId, {
            name,
            description,
          });
      if (saved) {
        setFormShown(false);
      }
    };

    const onDeleteGroup = async () => {
      if (!organizationId || !groupToDelete) return;
      await learnerGroupsStore!.deleteGroup(organizationId, groupToDelete.id);
      setGroupToDelete(undefined);
    };

    const renderEmpty = () => {
      if (learnerGroupsStore!.isLoading) {
        return (
          <div className={styles.loading}>
            <LoadingSpinner size={48} />
          </div>
        );
      }
      return search ? (
        <LearnersEmpty title={learnerGroupsText.cantFindGroups} />
      ) : (
        <LearnersEmpty
          title={learnerGroupsText.emptyGroupsTitle}
          text={learnerGroupsText.emptyGroupsText}
          actionTitle={learnerGroupsText.newGroup}
          onAction={onNewGroup}
        />
      );
    };

    return (
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <LearnersSearch value={search} onChange={setSearch} />
          <Button
            title={learnerGroupsText.newGroup}
            uiType="action"
            onClick={onNewGroup}
          />
        </div>
        <DataTable
          data={groups}
          columns={groupsColumns(
            onManageMembers,
            onEditGroup,
            setGroupToDelete,
          )}
          className="DataTable"
          theme="custom-stripes"
          striped
          noDataComponent={renderEmpty()}
        />
        {formFade.rendered && (
          <ModalFade closing={formFade.closing}>
            <GroupFormModal
              key={groupToEdit?.id ?? 'new'}
              group={groupToEdit}
              isLoading={learnerGroupsStore!.isLoading}
              onSubmit={onSubmitForm}
              onClose={() => setFormShown(false)}
            />
          </ModalFade>
        )}
        {membersFade.rendered && groupToManage && (
          <ModalFade closing={membersFade.closing}>
            <GroupMembersModal group={groupToManage} onClose={closeMembers} />
          </ModalFade>
        )}
        {deleteFade.rendered && (
          <ModalFade closing={deleteFade.closing}>
            <ConfirmationModal
              shown
              onAccept={onDeleteGroup}
              onCancel={() => setGroupToDelete(undefined)}
              acceptText={learnerGroupsText.delete}
              title={learnerGroupsText.deleteGroupConfirmation}
            />
          </ModalFade>
        )}
      </div>
    );
  }),
);

export default GroupsManager;
